import React, { useState } from "react"
import { useHistory } from "react-router-dom"
import { Account, DioboxEvent, TextMessage } from "sharedTypes"
import { useTextMessage } from "queries/texts"
import Spinner from "components/elements/Spinner"
import { useMutation, useQueryClient } from "react-query"
import * as api from "api/texts"
import { refetchTextMessages, updateTextMessage } from "context/actions"
import { eventTextingPath, eventTextMessagePath } from "utilities/routes"
import { generateDuplicateName } from "utilities/strings"
import { successToast } from "utilities/toasts"
import useFeatureFlag from "services/featureFlags"
import useWhatsAppTemplate from "services/useWhatsAppTemplate"
import DraftTextMessage from "./DraftTextMessage/DraftTextMessage"
import SentTextMessage from "./SentTextMessage/SentTextMessage"

export const TEXT_MESSAGE_BODY_CHARACTER_LIMIT = 290

type Props = {
  textMessageId: string
  event: DioboxEvent
  enabled: boolean
  emailEnabled: boolean
  smsCredits: number
  currentAccount: Account
  onClickAddCredits: () => void
}

const SelectedTextMessage = ({
  textMessageId,
  event: { id: eventId },
  event,
  enabled,
  emailEnabled,
  smsCredits,
  currentAccount,
  onClickAddCredits,
}: Props) => {
  const {
    isLoading: textMessageIsLoading,
    data: selectedTextMessage,
  } = useTextMessage(eventId, textMessageId)
  const queryClient = useQueryClient()
  const history = useHistory()
  const [showSavedLabel, setShowSavedLabel] = useState(false)
  const { whatsappMessaging: whatsAppEnabled } = useFeatureFlag()

  const { template } = useWhatsAppTemplate(selectedTextMessage?.whatsappContentSid)
  const isOptInText = template?.messageType === "opt_in"

  const { mutate: handleDelete } = useMutation(
    () => api.deleteTextMessage(eventId, textMessageId), {
      onSuccess: () => {
        refetchTextMessages(queryClient, eventId)
        history.push(eventTextingPath(eventId))
      },
    },
  )

  const handleSave = async (textMessage: TextMessage, { silent = false } = {}) => {
    const { data } = await api.putTextMessage(eventId, textMessageId, textMessage)

    if (!silent) {
      setShowSavedLabel(true)
      setTimeout(() => {
        setShowSavedLabel(false)
      }, 2000)
    }

    updateTextMessage(queryClient, textMessageId, data)
    refetchTextMessages(queryClient, eventId)
  }

  const handleDuplicate = async (textMessage: TextMessage) => {
    const duplicateName = generateDuplicateName(textMessage.name, {})

    const { data: { externalId } } = await api.postTextMessage(eventId,
      { ...textMessage, name: duplicateName })
    await refetchTextMessages(queryClient, eventId)

    history.push(eventTextMessagePath(eventId, externalId))
    successToast({ title: "Text message successfully duplicated" })
  }

  if (textMessageIsLoading) {
    return <Spinner />
  }

  if (!selectedTextMessage) {
    return null
  }

  // Redirect away from WhatsApp messages when the flag is off
  if (!whatsAppEnabled && selectedTextMessage.channel === "whatsapp") {
    history.replace(eventTextingPath(eventId))

    return null
  }

  // Defensive: legacy opt-in rows may have sent_at populated from before
  // Send All was removed on opt-in. Always route them to the draft view.
  if (selectedTextMessage?.sentAt && !isOptInText) {
    return (
      <SentTextMessage
        selectedTextMessage={selectedTextMessage}
        onSave={handleSave}
        onDelete={handleDelete}
        event={event}
        onDuplicate={handleDuplicate}
      />
    )
  }

  return (
    <DraftTextMessage
      key={textMessageId}
      event={event}
      selectedTextMessage={selectedTextMessage}
      onSave={handleSave}
      onDelete={handleDelete}
      showSavedLabel={showSavedLabel}
      onDuplicate={handleDuplicate}
      enabled={enabled}
      emailEnabled={emailEnabled}
      smsCredits={smsCredits}
      currentAccount={currentAccount}
      onClickAddCredits={onClickAddCredits}
    />
  )
}

export default SelectedTextMessage
